import {
  Autocomplete,
  Chip,
  Icon,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {Grid, useTheme} from "@mui/system";
import AttachFiles from "components/AttachFiles";
import {EmailData} from "components/EmailPreviewModal/EmailPreviewModal";
import useDownloadFromGuid from "hooks/useDownloadFromGuid";
import pxToRem from "hooks/usePxToRem";
import {CustomInput, CustomInputWrapper} from "ui";

interface EmailFieldsProps {
  emailData: EmailData;
  setEmailData: React.Dispatch<React.SetStateAction<EmailData>>;
  processId: number;
  handleChangeSubject: (_: unknown, nv: any, field: string) => void;
  handleAutoComplete: (_: unknown, nv: any, field: string) => void;
  handleClickFileChip: (item: {
    name: string;
    url: string;
    id?: string;
    guid?: string;
  }) => void;
  handleConfirmRemoveAttach: (id: string) => void;
  disableContent?: boolean;
}

const EmailFields = (props: EmailFieldsProps) => {
  const {
    emailData,
    setEmailData,
    processId,
    handleChangeSubject,
    handleAutoComplete,
    handleClickFileChip,
    handleConfirmRemoveAttach,
    disableContent,
  } = props;

  const theme = useTheme();
  const {handleDownloadFromGuid} = useDownloadFromGuid();

  return (
    <>
      <CustomInputWrapper title="Assunto" xs={12} isDisabled={disableContent}>
        <CustomInput
          name="subject"
          value={emailData?.subject ?? ""}
          placeholder="Assunto"
          size="small"
          required
          onChange={(e) => handleChangeSubject(e, e.target.value, "subject")}
          disabled={disableContent}
        />
      </CustomInputWrapper>
      <Stack
        direction="row"
        gap={pxToRem(8)}
        alignItems="center"
        sx={{
          backgroundColor: theme.palette.warning[100],
          padding: pxToRem(10),
          borderRadius: pxToRem(5),
          width: "100%",
        }}
      >
        <Icon color={"warning"} fontSize="small">
          info
        </Icon>

        <Typography
          color={theme.palette.warning[600]}
          sx={{fontSize: pxToRem(10)}}
        >
          {`Em caso de erro de envio do email, tente remover o rementente dos campos "para", "cc" ou "bcc". Uma cópia é enviada automaticamente ao remetente.`}
        </Typography>
      </Stack>
      <CustomInputWrapper title="Para" xs={12} isDisabled={disableContent}>
        <Autocomplete
          multiple
          size="small"
          placeholder="Para"
          freeSolo
          autoSelect
          value={emailData?.to ?? []}
          limitTags={5}
          onChange={(e, newValue) => handleAutoComplete(e, newValue, "to")}
          renderInput={(params) => (
            <TextField {...params} required={emailData?.to?.length === 0} />
          )}
          options={[]}
          disabled={disableContent}
        />
      </CustomInputWrapper>
      <CustomInputWrapper title="CC" xs={12} isDisabled={disableContent}>
        <Autocomplete
          multiple
          size="small"
          placeholder="CC"
          freeSolo
          autoSelect
          value={emailData?.cc ?? []}
          onChange={(e, newValue) => handleAutoComplete(e, newValue, "cc")}
          renderInput={(params) => <TextField {...params} />}
          options={[]}
          disabled={disableContent}
        />
      </CustomInputWrapper>
      <CustomInputWrapper title="BCC" xs={12} isDisabled={disableContent}>
        <Autocomplete
          multiple
          size="small"
          placeholder="BCC"
          freeSolo
          autoSelect
          value={emailData?.bcc ?? []}
          onChange={(e, newValue) => handleAutoComplete(e, newValue, "bcc")}
          renderInput={(params) => <TextField {...params} />}
          options={[]}
          disabled={disableContent}
        />
      </CustomInputWrapper>

      <CustomInputWrapper title="Anexos" xs={12} isDisabled={disableContent}>
        <AttachFiles
          emailData={emailData}
          setEmailData={setEmailData}
          processId={processId}
          disabled={disableContent}
        />
        {emailData?.attachment?.length === 0 && (
          <Typography variant="subtitle3">Sem anexos</Typography>
        )}
        <Grid container size={12} gap={pxToRem(8)} sx={{display: "flex"}}>
          {emailData?.attachment?.map((item) => (
            <Grid
              size={12}
              key={item?.id ?? item?.name}
              sx={{marginBottom: pxToRem(8)}}
            >
              <Tooltip title={item?.name} arrow placement="top">
                <Chip
                  label={item?.name}
                  size="medium"
                  variant="filled"
                  clickable
                  onClick={() => {
                    if (item?.url) {
                      handleClickFileChip(item);
                    } else {
                      handleDownloadFromGuid(item.guid, +item.id, item.name);
                    }
                  }}
                  onDelete={() => handleConfirmRemoveAttach(item?.id)}
                  sx={{
                    backgroundColor: theme.palette.primary[50],
                    color:
                      theme.palette.mode === "dark"
                        ? theme.palette.primary[600]
                        : theme.palette.primary[700],
                    ":hover": {
                      backgroundColor: theme.palette.primary[50],
                      opacity: 0.9,
                    },
                    "& .MuiChip-deleteIcon": {
                      marginLeft: pxToRem(10),
                    },
                  }}
                />
              </Tooltip>
            </Grid>
          ))}
        </Grid>
      </CustomInputWrapper>
    </>
  );
};

export default EmailFields;
